import useLocalOverwrite from '@/composables/saved-views/local-overwrite';
import usePageExport from '@/composables/export/page-export';
import { isEqual as isEqualLodash } from 'lodash-es';
import { savedView as savedViewConfig } from 'shared/api/query/configs.json';
import { sortAlphaNumeric, sortByAttr } from 'shared/lib/sort';

const SAVED_VIEW_LOCAL_OVERWRITE = 'saved_view_local_overwrite_v3';

export default function useLocalOverwritesParams(localStorage, loggedInUserAccount) {
  const { isPrint } = usePageExport();
  const localStorageKey = `${loggedInUserAccount.value.uid}_${SAVED_VIEW_LOCAL_OVERWRITE}`;

  const localOverwrites = useLocalOverwrite(savedViewConfig.edges.params, localStorageKey, localStorage);

  const cleanProps = (props) => {
    if (props === undefined) {
      return [];
    }
    return props.map((p) => ({ key: p.key, show: p.show, width: p.width, progressDisplayOption: p.progressDisplayOption })).sort(sortByAttr('key', sortAlphaNumeric));
  };

  const cleanUserScopeTree = (children) => children.map((c) => ({
    ...c,
    children: c.children ? cleanUserScopeTree(c.children) : [],
    scope: c.scope ? {
      number: null,
      relations: [],
      selectedOptions: [],
      timestamp: null,
      users: [],
      spaces: [],
      ...c.scope,
      property: { uid: c.scope.property?.uid, model: c.scope.property?.model },
    } : undefined,
  }));

  const cleanArray = (array) => {
    if (!Array.isArray(array)) {
      return [];
    }

    return array;
  };

  const isEqual = (a, b, key) => {
    if (key === 'order') {
      return isEqualLodash(cleanArray(a), cleanArray(b));
    }
    if (key === 'quickFilters') {
      return isEqualLodash(cleanArray(a), cleanArray(b));
    }
    if (key === 'props') {
      return isEqualLodash(cleanProps(a), cleanProps(b));
    }
    return isEqualLodash(a, b);
  };

  const removedFilters = (overwrite, original) => original.reduce((acc, child) => {
    const ov = overwrite.find((o) => o.key === child.key);
    if (ov === undefined) {
      acc.push(child);
    }
    return acc;
  }, []);

  const changedOrAddedFilters = (overwrite, original) => overwrite.reduce((acc, child) => {
    const originalChild = original.find((o) => o.key === child.key);
    if (originalChild === undefined) {
      acc.push(child.key);
      return acc;
    }
    if (isEqualLodash(child, originalChild)) {
      return acc;
    }
    acc.push(child.key);
    return acc;
  }, []);

  const hasLocalOverwrite = (originalView) => {
    if (isPrint.value) {
      return [];
    }

    const overwrite = localOverwrites.findLocalOverwrite(originalView);
    if (overwrite === undefined) {
      return [];
    }

    const ignoreFields = ['showParents'];
    return Object.keys(overwrite).reduce((res, next) => {
      if (ignoreFields.includes(next)) {
        return res;
      }
      if (next === 'filter') {
        const d = [
          ...changedOrAddedFilters(cleanUserScopeTree(overwrite[next].children), cleanUserScopeTree(originalView.params[next]?.children || [])),
          ...removedFilters(overwrite[next].children, originalView.params[next]?.children || []),
        ];
        if (d.length > 0) {
          res.push(next);
        }
        res.push(...d);
        return res;
      }
      if (isEqual(overwrite[next], originalView.params[next], next)) {
        return res;
      }
      res.push(next);
      return res;
    }, []);
  };

  const mergeWithLocalOverwrites = (view) => {
    const overwrite = localOverwrites.mergeWithLocalOverwrites(view);

    return {
      ...view,
      params: {
        ...view.params,
        ...overwrite.params,
      },
    };
  };

  return {
    saveOverwrite: localOverwrites.saveOverwrite,
    removeLocalOverwrite: localOverwrites.removeLocalOverwrite,
    mergeWithLocalOverwrites,
    initOverwrites: localOverwrites.initOverwrites,

    hasLocalOverwrite,
  };
}
